import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  Package, 
  Search, 
  Plus, 
  Filter, 
  MoreVertical, 
  Edit2, 
  Trash2, 
  CheckCircle2, 
  AlertCircle,
  Tag,
  DollarSign,
  Layers,
  Database,
  Sparkles,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import TenantContext from '@/contexts/TenantContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const InventoryPage = () => {
    const tenantContext = useContext(TenantContext);
    const location = useLocation();
    const contextTenantId = tenantContext?.tenant?.id;

    // Estado local para permitir la selección manual (Dashboard Maestro)
    const [selectedTenantId, setSelectedTenantId] = useState('default');

    // Efecto para capturar navegación desde Arquitectura Engine
    useEffect(() => {
        if (location.state?.tenantId) {
            setSelectedTenantId(location.state.tenantId);
        }
    }, [location.state]);
    
    const [products, setProducts] = useState([]);
    const [tenants, setTenants] = useState([]); // Solo para admins
    const [silos, setSilos] = useState([]);
    const [selectedSilo, setSelectedSilo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    
    // Si hay un tenant en el contexto, lo usamos. Si no, usamos el seleccionado manualmente.
    const tenantId = contextTenantId || selectedTenantId;
    const tenantSlug = tenantContext?.tenantSlug || tenantId.replace('tenant_', '');
    
    // Estados para el buscador de tenants (Solo visible para super-admins)
    const [showTenantSelector, setShowTenantSelector] = useState(false);
    const [tenantSearch, setTenantSearch] = useState('');

    // Nuevo estado para la Modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [newItem, setNewItem] = useState({ nombre: '', codigo: '', precio_publico: '', categoria: '', descripcion: '' });

    useEffect(() => {
        // En un entorno de portal de cliente, esto podría estar restringido
        fetchTenants();
    }, []);

    useEffect(() => {
        if (tenantId && tenantId !== 'default') {
            fetchTenantDetails();
        }
    }, [tenantId, tenantSlug]);

    useEffect(() => {
        if (selectedSilo) {
            fetchSiloData();
        }
    }, [selectedSilo, tenantId]);

    const fetchTenants = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/tenants`);
            setTenants(response.data || []);
        } catch (error) {
            console.error('Error fetching tenants:', error);
        }
    };

    const fetchTenantDetails = async () => {
        try {
            // Usamos el portal-info o el endpoint de tenant ID
            const targetId = tenantId.replace('tenant_', ''); // Limpieza por si acaso
            const response = await axios.get(`${API_URL}/api/tenants/${targetId}`);
            const tenantSilos = response.data.silos_directory || [];
            setSilos(tenantSilos);
            
            // Prioridad: 1. Silo de productos, 2. El primer silo que exista, 3. Fallback estandar
            if (tenantSilos.length > 0) {
                const productSilo = tenantSilos.find(s => s.name.toLowerCase().includes('productos'));
                setSelectedSilo(productSilo || tenantSilos[0]);
            } else {
                // Si es un tenant virgen post-Fénix, creamos la referencia estándar
                const defaultSiloName = `${tenantSlug}_productos`;
                setSelectedSilo({ 
                    name: defaultSiloName, 
                    label: '🛒 Catálogo de Productos',
                    count: 0 
                });
            }
        } catch (error) {
            console.error('Error fetching tenant details:', error);
            // Fallback silencioso para no romper la UX
            setSelectedSilo({ 
                name: `${tenantSlug}_productos`, 
                label: '🛒 Catálogo de Productos' 
            });
        }
    };

    const fetchSiloData = async () => {
        if (!selectedSilo) return;
        try {
            setLoading(true);
            const response = await axios.post(`${API_URL}/api/silos/search`, {
                tenant_id: tenantId,
                collection_name: selectedSilo.name,
                query: {} 
            });
            setProducts(response.data || []);
        } catch (error) {
            console.error('Error fetching silo data:', error);
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenCreate = () => {
        setNewItem({ nombre: '', codigo: '', precio_publico: '', categoria: '', descripcion: '' });
        setIsEditing(false);
        setIsModalOpen(true);
    };

    const handleOpenEdit = (product) => {
        setNewItem({ ...product });
        setIsEditing(true);
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        if(!newItem.nombre || !newItem.codigo || !newItem.precio_publico) {
            toast.error('Nombre, Código y Precio son obligatorios');
            return;
        }
        try {
            setSaving(true);
            const productData = {
               ...newItem,
               precio_publico: parseFloat(newItem.precio_publico) || 0,
               precio: parseFloat(newItem.precio_publico) || 0,
               activo: true,
               stock: 99,
               engine_injected_at: new Date().toISOString()
            };
            
            // Endpoint unificado de inventario que maneja la colección por debajo
            if (isEditing) {
                await axios.put(`${API_URL}/api/v1/inventory/${tenantId}/${newItem.codigo}`, productData);
                toast.success('¡Información actualizada!');
            } else {
                await axios.post(`${API_URL}/api/v1/inventory/${tenantId}`, productData);
                toast.success('¡Producto guardado en el catálogo!');
            }
            
            setIsModalOpen(false);
            setNewItem({ nombre: '', codigo: '', precio_publico: '', categoria: '', descripcion: '' });
            fetchSiloData();
        } catch (error) {
            toast.error('Hubo un error al guardar. Reintenta en un momento.');
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (codigo) => {
        if(!window.confirm(`¿Estás seguro de eliminar este registro (${codigo})?`)) return;
        try {
            await axios.delete(`${API_URL}/api/v1/inventory/${tenantId}/${codigo}`);
            toast.success('Eliminado correctamente');
            fetchSiloData();
        } catch (error) {
            toast.error('No se pudo eliminar el registro');
            console.error(error);
        }
    };

    const categories = ['All', ...new Set(products.map(p => p.categoria || 'Sin Categoría'))];

    const filteredTenants = tenants.filter(t => 
        t.name.toLowerCase().includes(tenantSearch.toLowerCase()) || 
        t.id.toLowerCase().includes(tenantSearch.toLowerCase())
    );

    const activeTenant = tenants.find(t => t.id === tenantId) || { name: 'Seleccionar Tenant', id: tenantId };

    const filteredProducts = products.filter(item => {
        // Búsqueda genérica en cualquier campo de texto
        const searchStr = searchTerm.toLowerCase();
        const matchesSearch = Object.values(item).some(val => 
            String(val).toLowerCase().includes(searchStr)
        );
        
        const matchesCategory = selectedCategory === 'All' || item.categoria === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const isEngineReady = silos && silos.length > 0;

    return (
        <div className="p-6 space-y-8 animate-in fade-in duration-700 relative">
            {/* Modal de Item (Nuevo/Editar) */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-[#0B1120] border border-slate-800/80 rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl shadow-blue-900/20">
                        <div className="p-6 pb-0 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                {isEditing ? <Edit2 className="w-5 h-5 text-blue-500" /> : <Package className="w-5 h-5 text-blue-500" />}
                                {isEditing ? 'Editar Producto' : 'Agregar a Inventario'}
                            </h2>
                            <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-slate-800 rounded-full text-slate-400 hover:text-white transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="space-y-1">
                                <label className="text-xs text-slate-400 font-medium">Nombre del Servicio/Producto</label>
                                <input value={newItem.nombre} onChange={e => setNewItem({...newItem, nombre: e.target.value})} type="text" className="w-full bg-slate-900 border border-slate-800 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50" placeholder="Ej. Suscripcion Premium" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-xs text-slate-400 font-medium">Codigo Unico</label>
                                    <input 
                                        value={newItem.codigo} 
                                        onChange={e => setNewItem({...newItem, codigo: e.target.value})} 
                                        disabled={isEditing}
                                        type="text" 
                                        className={`w-full bg-slate-900 border border-slate-800 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${isEditing ? 'opacity-50 cursor-not-allowed' : ''}`} 
                                        placeholder="Ej. SUB-PRM-1" 
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs text-slate-400 font-medium">Costo ($ MXN)</label>
                                    <input value={newItem.precio_publico} onChange={e => setNewItem({...newItem, precio_publico: e.target.value})} type="number" className="w-full bg-slate-900 border border-slate-800 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50" placeholder="Ej. 1500" />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs text-slate-400 font-medium">Categoria</label>
                                <input value={newItem.categoria} onChange={e => setNewItem({...newItem, categoria: e.target.value})} type="text" className="w-full bg-slate-900 border border-slate-800 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50" placeholder="Ej. Licencias" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs text-slate-400 font-medium">Descripcion Breve</label>
                                <textarea value={newItem.descripcion} onChange={e => setNewItem({...newItem, descripcion: e.target.value})} rows="2" className="w-full bg-slate-900 border border-slate-800 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50" placeholder="Opcional..."></textarea>
                            </div>
                        </div>
                        <div className="p-6 pt-4 bg-slate-900/50 border-t border-slate-800 flex justify-end gap-3">
                            <button onClick={() => setIsModalOpen(false)} className="px-5 py-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 font-medium transition-all">Cancelar</button>
                            <button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white px-5 py-2 rounded-xl font-medium shadow-lg shadow-blue-500/20 transition-all flex items-center gap-2">
                                {saving ? 'Guardando...' : (isEditing ? 'Actualizar Cambios' : 'Guardar Item')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-white flex items-center gap-2">
                        <Database className="w-8 h-8 text-blue-500" />
                        Fluxo Data <span className="text-blue-500">Engine</span>
                    </h1>
                    <p className="text-muted-foreground mt-1 text-sm md:text-base">
                        {isEngineReady 
                            ? `Gestiona tu información en ${activeTenant.name}.`
                            : `Esperando configuración técnica para ${activeTenant.name}.`
                        }
                    </p>
                </div>
                <button 
                  onClick={handleOpenCreate}
                  disabled={!isEngineReady}
                  className={`hidden md:flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-lg active:scale-95 ${
                    isEngineReady
                    ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-900/20'
                    : 'bg-slate-800 text-slate-500 cursor-not-allowed opacity-50'
                  }`}
                >
                  <Plus className="w-4 h-4" />
                  Nuevo Registro
                </button>
            </div>

            {/* Silo Selection Tabs */}
            <div className="flex flex-wrap gap-2 p-1 bg-slate-900/20 rounded-2xl w-fit border border-slate-800/30">
                {silos.map((silo) => (
                    <button
                        key={silo.name}
                        onClick={() => setSelectedSilo(silo)}
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                            selectedSilo?.name === silo.name 
                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' 
                            : 'text-slate-400 hover:text-white hover:bg-slate-800'
                        }`}
                    >
                        <Layers className="w-4 h-4" />
                        {silo.label}
                    </button>
                ))}
            </div>

            {/* Controls Bar - forcing high z-index for dropdowns */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-slate-900/40 backdrop-blur-md p-4 rounded-2xl border border-slate-800/50 shadow-xl relative z-30">
                <div className="md:col-span-2 relative group">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 group-focus-within:text-emerald-400 transition-colors" />
                    <input 
                        type="text" 
                        placeholder="Buscar por nombre o codigo..."
                        className="w-full bg-slate-950/50 border border-white/10 rounded-xl py-2 pl-10 pr-4 text-white focus:ring-0 focus:border-emerald-500/40 focus:shadow-[0_0_20px_rgba(16,185,129,0.15)] focus:outline-none transition-all duration-300"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="relative">
                    <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <select 
                        className="w-full bg-slate-950/50 border border-slate-800 rounded-xl py-2 pl-10 pr-4 text-white appearance-none focus:ring-2 focus:ring-blue-500/40 focus:outline-none cursor-pointer"
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                        {categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                </div>
                <div className={`relative ${showTenantSelector ? 'z-[100]' : 'z-10'}`}>
                    <Layers className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <button 
                        onClick={() => setShowTenantSelector(!showTenantSelector)}
                        className="w-full bg-slate-950/60 border border-slate-800 rounded-xl py-2.5 pl-10 pr-10 text-white text-sm text-left focus:ring-2 focus:ring-blue-500/40 focus:outline-none cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap transition-all hover:bg-slate-900"
                    >
                        {activeTenant.name || 'Seleccionar Cliente'}
                        <MoreVertical className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    </button>
                    
                    {showTenantSelector && (
                        <>
                            <div className="fixed inset-0 z-40 bg-black/5" onClick={() => setShowTenantSelector(false)}></div>
                            <div className="absolute top-full left-0 right-0 mt-2 z-[1000] bg-[#0F172A] border border-slate-800/80 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden animate-in slide-in-from-top-2 duration-200 min-w-[200px]">
                                <div className="p-3 border-b border-slate-800 bg-slate-900/50">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <input 
                                            autoFocus
                                            type="text"
                                            placeholder="Buscar cliente..."
                                            className="w-full bg-slate-950 border border-slate-800 rounded-lg py-2 pl-9 pr-3 text-sm text-white focus:ring-1 focus:ring-blue-500/50 focus:outline-none placeholder:text-slate-600"
                                            value={tenantSearch}
                                            onChange={(e) => setTenantSearch(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="max-h-[300px] overflow-y-auto p-1.5 bg-[#0F172A]">
                                    {filteredTenants.length > 0 ? filteredTenants.map(t => (
                                        <button 
                                            key={t.id}
                                            onClick={() => {
                                                setSelectedTenantId(t.id);
                                                setShowTenantSelector(false);
                                                setTenantSearch('');
                                            }}
                                            className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-all flex flex-col gap-0.5 mb-1 last:mb-0 ${tenantId === t.id ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}`}
                                        >
                                            <span className="font-semibold">{t.name}</span>
                                            <span className={`text-[10px] font-mono ${tenantId === t.id ? 'text-blue-100' : 'text-slate-500'}`}>{t.id}</span>
                                        </button>
                                    )) : (
                                        <div className="text-center py-6 text-slate-500 flex flex-col items-center gap-2">
                                            <AlertCircle className="w-5 h-5 opacity-50" />
                                            <span className="text-xs">No se encontraron clientes</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Products Grid */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
                    {[1, 2, 3, 4, 5, 6].map(i => (
                        <div key={i} className="h-48 bg-slate-900/50 rounded-3xl border border-slate-800/50"></div>
                    ))}
                </div>
            ) : filteredProducts.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredProducts.map((item, idx) => {
                        // Detectar campos clave dinámicamente
                        const title = item.nombre || item.name || item.title || Object.values(item)[1] || 'Sin Título';
                        const subtitle = item.codigo || item.id || item.code || item.sku || '';
                        const description = item.descripcion || item.description || item.bio || '';
                        const price = item.precio_publico || item.precio || item.price || null;
                        const category = item.categoria || item.category || item.type || selectedSilo?.label || 'General';

                        return (
                            <div 
                                key={idx}
                                className="group relative bg-[#0B1120]/60 backdrop-blur-xl border border-slate-800/50 hover:border-blue-500/40 rounded-3xl p-6 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-900/10 hover:-translate-y-1 overflow-hidden"
                            >
                                {/* Accent blur */}
                                <div className="absolute -top-10 -right-10 w-32 h-32 bg-blue-600/10 blur-3xl rounded-full group-hover:bg-blue-600/20 transition-all duration-500"></div>
                                
                                <div className="relative z-10 space-y-4">
                                    <div className="flex justify-between items-start">
                                        <div className="p-3 bg-blue-500/10 rounded-2xl">
                                            {price ? <DollarSign className="w-6 h-6 text-blue-500" /> : <Layers className="w-6 h-6 text-blue-500" />}
                                        </div>
                                        <div className="flex gap-2">
                                            <button 
                                                onClick={() => handleOpenEdit(item)}
                                                className="p-2 hover:bg-slate-800 rounded-xl transition-colors text-slate-400 hover:text-white"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(item.codigo || item.id || item.code)}
                                                className="p-2 hover:bg-red-500/10 rounded-xl transition-colors text-slate-400 hover:text-red-500"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-blue-500/80 bg-blue-500/5 px-2 py-0.5 rounded-full border border-blue-500/10">
                                                {category}
                                            </span>
                                            {subtitle && (
                                                <span className="text-[10px] font-mono text-slate-500">
                                                    {subtitle}
                                                </span>
                                            )}
                                        </div>
                                        <h3 className="text-xl font-bold text-white mt-1 group-hover:text-blue-400 transition-colors">
                                            {title}
                                        </h3>
                                        <p className="text-slate-400 text-sm mt-2 line-clamp-2 min-h-[40px]">
                                            {description || 'No hay descripción disponible para este registro.'}
                                        </p>
                                    </div>

                                    <div className="pt-4 flex items-center justify-between border-t border-slate-800/50">
                                        <div className="flex items-baseline gap-1">
                                            {price ? (
                                                <>
                                                    <span className="text-xs text-slate-500 font-medium">$</span>
                                                    <span className="text-2xl font-black text-white tracking-tight">
                                                        {Number(price).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                                    </span>
                                                    <span className="text-[10px] text-slate-500 ml-1 font-bold">MXN</span>
                                                </>
                                            ) : (
                                                <span className="text-xs text-slate-500 italic">Registro Multi-data</span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1.5 px-3 py-1 bg-emerald-500/5 border border-emerald-500/20 rounded-full">
                                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                                            <span className="text-[10px] font-bold text-emerald-500 uppercase tracking-tighter">Sincronizado</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-24 text-center space-y-8 bg-[#0B1120]/40 rounded-[3rem] border border-dashed border-slate-800/60 transition-all hover:bg-[#0B1120]/60 group">
                    <div className="relative">
                        <div className="absolute inset-0 bg-blue-500/20 blur-3xl rounded-full group-hover:bg-blue-500/30 transition-all duration-700" />
                        <div className="relative p-8 bg-blue-500/10 rounded-full border border-blue-500/20 group-hover:scale-110 transition-transform duration-500">
                            <Sparkles className="w-16 h-16 text-blue-500 animate-pulse" />
                        </div>
                    </div>
                    <div className="max-w-md space-y-3">
                        <h3 className="text-3xl font-black text-white tracking-tight uppercase italic">
                            {isEngineReady ? 'El catálogo está listo' : 'Configuración en proceso'}
                        </h3>
                        <p className="text-slate-500 font-medium">
                            {isEngineReady 
                                ? 'Tu almacén inteligente está configurado y esperando información. Empieza a cargar tus productos.'
                                : 'Estamos preparando tu motor de datos. Por favor, contacta a soporte para activar tu primer almacén.'
                            }
                        </p>
                    </div>
                    <button 
                        onClick={handleOpenCreate}
                        disabled={!isEngineReady}
                        className={`flex items-center gap-3 px-10 py-5 rounded-2xl font-black text-sm uppercase tracking-widest transition-all shadow-2xl active:scale-95 translate-y-2 group-hover:translate-y-0 ${
                            isEngineReady 
                            ? 'bg-white text-black hover:bg-blue-500 hover:text-white hover:shadow-blue-500/40' 
                            : 'bg-slate-800 text-slate-500 cursor-not-allowed opacity-50'
                        }`}
                    >
                        <Plus className="w-6 h-6" />
                        {isEngineReady ? 'Cargar mi primer producto' : 'Módulo en Preparación'}
                    </button>
                    <p className="text-[10px] font-black text-slate-700 uppercase tracking-[0.3em]">Felicidad 100% Garantizada</p>
                </div>
            )}
        </div>
    );
};

export default InventoryPage;
